import { useQuery } from '@apollo/client'

import { extractByTypename } from '@local/utils'

import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { MySectionType, ShortcutSectionContainer_UserFragment, TimelineTab } from '@web/federation/gql/graphql'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { useGoTo } from '@web/hooks/useGoTo'
import { useNavigation } from '@web/hooks/useNavigation'
import { messages } from '@web/libs/constants'
import routes from '@web/libs/constants/routes'
import { neloLogger } from '@web/libs/utils'
import { MySectionsQuery } from '@web/pages/v5/libs/graphql/MySections'
import { Nullish } from '@web/types'
import ShortcutSection, { ShortcutSectionProps } from '@web/ui/pages/home/components/Sections/ShortcutSection'

type Props = {
  user: Nullish<ShortcutSectionContainer_UserFragment>
}

const useConvertProps = (props: Props): ShortcutSectionProps => {
  const { data } = useQuery(MySectionsQuery)
  const { user } = props
  const { goToPlaceTab } = useGoTo()
  const { MySectionsSucceed } = extractByTypename(data?.mySections)
  const { ShortcutSection } = extractByTypename(
    MySectionsSucceed?.sections.find(section => section.type === MySectionType.ShortcutSection),
  )

  const navigation = useNavigation()
  const { isFromMapsApp } = useGlobalStaticStateContext()
  const { bookmarkPageLandingUrl, couponPageUrl } = ShortcutSection ?? {}

  const handleClickInnerLink = (url: string) => {
    if (!user) return navigation.goToLogin()

    return navigation.push({ url })
  }

  const handleClickOutsideLink = (url: string) => {
    if (!user) return navigation.goToLogin()

    // 외부 페이지일 경우 새창으로 이동
    return navigation.push({ url, openWindow: true })
  }

  return {
    bookingCount: user?.bookingStat?.upcomingBookingCount ?? 0,
    orderCount: user?.bookingStat?.upcomingOrderCount ?? 0,
    couponCount: user?.bookingStat?.couponCount ?? 0,
    isDisplayBookmark: !isFromMapsApp,
    onOrderClick: () => {
      handleClickInnerLink(`${routes.TIMELINE_LIST}?tab=${TimelineTab.Order}&shortcut=true`)
    },
    onBookingClick: () => {
      handleClickInnerLink(`${routes.TIMELINE_LIST}?tab=${TimelineTab.Reservation}&shortcut=true`)
    },
    onCouponClick: () => {
      if (!couponPageUrl?.webUrl) {
        neloLogger('warn', `숏컷 - couponPageUrl 제공되지 않음`)
        openErrorToast(messages.TRY_AGIN)
        return
      }

      handleClickOutsideLink(couponPageUrl.webUrl)
    },
    onReviewClick: () => {
      handleClickInnerLink(routes.HOME_MY_REVIEW)
    },
    onBookmarkClick: () => {
      if (!bookmarkPageLandingUrl?.webUrl) {
        neloLogger('warn', `숏컷 - bookmarkPageLandingUrl 제공되지 않음`)
        openErrorToast(messages.TRY_AGIN)
        return
      }

      if (!user) {
        navigation.goToLogin()
        return
      }

      goToPlaceTab(user.idno)
    },
  }
}

const ShortcutSectionContainer = (props: Props) => {
  const shortcutProps = useConvertProps(props)

  return <ShortcutSection {...shortcutProps} />
}

export default ShortcutSectionContainer
